import { DEMO_WIDTH, DEMO_HEIGHT } from './layoutConstants.js';


/**
 * From https://web.dev/canvas-hidipi/ . High-DPI devices automatically scale CSS-level pixel-values up/down as needed;
 * apparently we need to set `scale` to make the same thing happen in canvases.
 */
export const initCanvas = (canvas) => {
    // Get the device pixel ratio, falling back to 1.
    const dpr = window.devicePixelRatio || 1;
    // Get the size of the canvas in CSS pixels.
    const rect = canvas.getBoundingClientRect();
    // Give the canvas pixel dimensions of their CSS
    // size * the device pixel ratio.
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    // Scale all drawing operations by the dpr, so you
    // don't have to worry about the difference.
    ctx.scale(dpr, dpr);

    return ctx;
}

/**
 * From https://codepen.io/smpnjn/pen/mdpEWEN . Use this function to decide how to divide up a long block of text that
 * you want to render on top of a canvas into multiple lines.
 *
 * @param ctx A CanvasRenderingContext2D
 * @param text The text you want to superimpose on the canvas
 * @param x X-coordinate of the _starting point_ of your text-block
 * @param y Y-coordinate of the _starting point_ of your text-block
 * @param maxWidth Start a new line once the current one is this wide
 * @param lineHeight How tall each line should be
 *
 * @returns {[*[],number]} A two-item array. The second item is just the total height of the full body of text once
 * it's drawn on the image. The first item is an array of arrays. Each item in this array represents one line of the
 * rendered text:
 *
 * [line, x, y]
 *
 * where "line" is the text content of a given line, x is its starting X coordinate, and y is its starting y coordinate.
 */
export const wrapText = function (ctx, text, x, y, maxWidth, lineHeight) {
    var words = text.split(' ');
    var line = '';
    let testLine = '';
    let wordArray = [];
    let totalLineHeight = 0;
    for (var n = 0; n < words.length; n++) {
        testLine += `${words[n]} `;
        var metrics = ctx.measureText(testLine);
        var testWidth = metrics.width;
        if (testWidth > maxWidth && n > 0) {
            wordArray.push([line, x, y]);
            y += lineHeight;
            totalLineHeight += lineHeight;
            line = `${words[n]} `;
            testLine = `${words[n]} `;
        } else {
            line += `${words[n]} `;
        }
        if (n === words.length - 1) {
            wordArray.push([line, x, y]);
        }
    }
    return [wordArray, totalLineHeight];
}



export const drawImageInCircle = (canvasContext, img, x, y, radius) => {
    canvasContext.save();

    // Set the "clipping region" to a circle with the size/location we want.
    // With that done, we'll only show the part of the image that fits in this circle.
    canvasContext.beginPath();
    canvasContext.arc(x, y, radius, 0, Math.PI * 2)
    canvasContext.clip();

    canvasContext.drawImage(img, x - radius, y - radius, radius * 2, radius * 2);

    // Undo our munging of the "clipping region"
    canvasContext.restore();
}

export const drawImageInBorderedCircle = (canvasContext, img, x, y, radius) => {
    canvasContext.save();

    // Draw a circle that's slightly larger than the image (to give the image a border)
    canvasContext.fillStyle = '#ffbe36';
    canvasContext.beginPath();
    canvasContext.arc(x, y, radius + 3, 0, Math.PI * 2)
    canvasContext.fill();

    drawImageInCircle(canvasContext, img, x, y, radius);
    canvasContext.restore();
}

export const drawTiledBackgroundImage = (canvasContext, img, canvas) => {
    canvasContext.save();
    // This sets global transparency to 50%
    canvasContext.globalAlpha = 0.5;

    // Note: this code assumes the background-photo is wider than our canvas. If that's not true we'll have to iterate horizontally too
    const rowCount = Math.ceil(canvas.height / img.height);
    for (let i = 0; i < rowCount; i++) {
        canvasContext.drawImage(img, 0, i * img.height, img.width, img.height);
    }

    // This blows away our global transparency change
    canvasContext.restore();
}

/**
 * Given something that was N pixels wide in the demo, return its proper width in whatever size canvas you're passing in.
 * You can also use this with starting/ending X-coordinates.
 *
 * Assumes you have already used device-pixel-ratio to set scale on this canvas context - see initCanvas.
 *
 * @param widthInDemo width of the specific item we're re-scaling *in the demo*
 * @param canvas
 * @returns {number}
 */
export const scaleWidth = (widthInDemo, canvas) => {
    return scale(widthInDemo, DEMO_WIDTH, canvas.getBoundingClientRect().width);
}

/**
 * See scaleWidth. This is that but for height.
 */
export const scaleHeight = (heightInDemo, canvas) => {
    return scale(heightInDemo, DEMO_HEIGHT, canvas.getBoundingClientRect().height);
}

const scale = (inputSize, demoSize, canvasSize) => {
    return Math.floor((inputSize * canvasSize) / demoSize);
}

/**
 * `roundRect` is a relatively new method. If the client doesn't support it, just draw a regular rectangle instead.
 *
 * @param ctx The CanvasRenderingContext2D to modify.
 * @param x
 * @param y
 * @param width
 * @param height
 * @param radii
 */
export const hopefullyRoundRect = (ctx, x, y, width, height, radii) => {
    if (ctx.roundRect) {
        ctx.roundRect(x, y, width, height, radii);
    } else {
        ctx.rect(x, y, width, height);
    }
}

const makeImageLoadedPromise = (image, src) => {
    return new Promise((resolve, reject) => {
        image.addEventListener('load', () => {
            resolve(image);
        });
        image.addEventListener('error', (error) => {
            reject(error);
        });

        image.src = src;
    });
}
const makeNoopPromise = () => {
    return new Promise((resolve, reject) => {
        resolve(null);
    });
}
/**
 * @param src An image-URL that might be null
 * @returns {Promise<unknown>} If the URL is non-null? A Promise that wraps an image. It resolves
 * when we finish making an image out of that URL. If the URL is null? A Promise that wraps nothing and resolves immediately.
 */
export const promiseFromNullable = (src) => {
    if (src == null) {
        return makeNoopPromise();
    } else {
        const img = new Image();
        // Add an "Origin" header for CORS enforcement, but don't send cookies.
        // (The server *should* be ignoring cookies for image-requests regardless,
        // but if that ever changes we want to fail fast.)
        img.setAttribute('crossorigin', 'anonymous');
        return makeImageLoadedPromise(img, src);
    }
}

